import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AnalyticsSummary {
  totalViews: number;
  totalClicks: number;
  totalArticles: number;
  avgTimeOnPage: number;
}

export interface TopArticle {
  articleId: string;
  headline: string;
  slug: string;
  tokens: string[];
  views: number;
  clicks: number;
  uniqueViews: number;
  avgTimeOnPage: number;
}

export interface DailyTrend {
  date: string;
  views: number;
  clicks: number;
  uniqueViews: number;
}

export function useAnalytics() {
  return useQuery({
    queryKey: ['analytics-summary'],
    queryFn: async () => {
      // Get all performance data with article details
      const { data: performanceData, error: perfError } = await supabase
        .from('article_performance')
        .select(`
          *,
          articles!inner (
            id,
            headline,
            slug,
            tokens_featured
          )
        `)
        .order('date', { ascending: false });

      if (perfError) throw perfError;

      // Calculate total metrics
      const totalViews = performanceData?.reduce((sum, row) => sum + (row.views || 0), 0) || 0;
      const totalClicks = performanceData?.reduce((sum, row) => sum + (row.affiliate_clicks || 0), 0) || 0;
      const avgTimeOnPage = performanceData?.length > 0
        ? performanceData.reduce((sum, row) => sum + (row.avg_time_on_page || 0), 0) / performanceData.length
        : 0;

      // Get unique article count
      const uniqueArticles = new Set(performanceData?.map(row => row.article_id)).size;

      const summary: AnalyticsSummary = {
        totalViews,
        totalClicks,
        totalArticles: uniqueArticles,
        avgTimeOnPage: Math.round(avgTimeOnPage)
      };

      // Aggregate top articles (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];

      const recentData = performanceData?.filter(row => row.date >= thirtyDaysAgoStr) || [];
      
      const articleStats = recentData.reduce((acc: Record<string, any>, row: any) => {
        const id = row.article_id;
        if (!id) return acc;
        
        if (!acc[id]) {
          acc[id] = {
            articleId: id,
            headline: row.articles.headline,
            slug: row.articles.slug,
            tokens: row.articles.tokens_featured || [],
            views: 0,
            clicks: 0,
            uniqueViews: 0,
            avgTimeOnPage: 0,
            count: 0
          };
        }
        
        acc[id].views += row.views || 0;
        acc[id].clicks += row.affiliate_clicks || 0;
        acc[id].uniqueViews += row.unique_views || 0;
        acc[id].avgTimeOnPage += row.avg_time_on_page || 0;
        acc[id].count += 1;
        
        return acc;
      }, {});

      const topArticles: TopArticle[] = Object.values(articleStats)
        .map((article: any) => ({
          ...article,
          avgTimeOnPage: Math.round(article.avgTimeOnPage / article.count)
        }))
        .sort((a: any, b: any) => b.views - a.views)
        .slice(0, 10);

      // Calculate daily trends (last 30 days)
      const dailyStats = recentData.reduce((acc: Record<string, any>, row: any) => {
        const date = row.date;
        if (!acc[date]) {
          acc[date] = {
            date,
            views: 0,
            clicks: 0,
            uniqueViews: 0
          };
        }
        acc[date].views += row.views || 0;
        acc[date].clicks += row.affiliate_clicks || 0;
        acc[date].uniqueViews += row.unique_views || 0;
        return acc;
      }, {});

      const dailyTrend: DailyTrend[] = Object.values(dailyStats)
        .sort((a: any, b: any) => a.date.localeCompare(b.date));

      return {
        summary,
        topArticles,
        dailyTrend
      };
    },
    refetchInterval: 60000 // Refresh every minute
  });
}
